// First-time-user onboarding state (welcome modal, spotlight tour, checklist).
// There is no first-login infra in the app, so this introduces a single
// localStorage flag (`cl.onboarding.v1`) plus a status self-fetch the checklist
// reads to auto-complete its steps. Mounted once in App.js, inside the Router.
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import portfolioService from '../../services/portfolioService';
import tradingBotService from '../../services/tradingBotService';

const KEY = 'cl.onboarding.v1';
const readFlags = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (_) { return {}; }
};
const writeFlags = (patch) => {
  const next = { ...readFlags(), ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
  return next;
};

const OnboardingContext = createContext(null);
// Safe even if a consumer renders outside the provider (returns {}).
export const useOnboarding = () => useContext(OnboardingContext) || {};

export function OnboardingProvider({ children }) {
  const [flags, setFlags] = useState(readFlags);
  const [tourRunning, setTourRunning] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [status, setStatus] = useState({ loading: true, hasExchange: false, hasLockedAssets: false, hasBot: false });
  const fetchingRef = useRef(false);

  const persist = useCallback((patch) => setFlags(writeFlags(patch)), []);

  // Derive checklist completion from the same services the pages use.
  //
  // Pass { fresh: true } when calling right after a mutation (locking assets,
  // creating a bot): the backend caches /api/portfolio/summary in Redis, so a
  // cached read can still report the pre-change state and leave the step
  // stubbornly unticked. The in-flight guard is skipped for those calls too,
  // otherwise a background refresh already running would swallow them.
  const refreshStatus = useCallback(async ({ fresh = false } = {}) => {
    if (!fresh && fetchingRef.current) return;
    if (localStorage.getItem('authenticated') !== 'true') {
      setStatus((s) => ({ ...s, loading: false }));
      return;
    }
    fetchingRef.current = true;
    try {
      const userId = localStorage.getItem('userId');
      const [summary, bots] = await Promise.allSettled([
        portfolioService.summary({ fresh }),
        tradingBotService.list(userId),
      ]);
      const exchanges = summary.status === 'fulfilled' ? (summary.value?.exchanges || []) : [];
      const botList = bots.status === 'fulfilled'
        ? (Array.isArray(bots.value) ? bots.value : (bots.value?.bots || []))
        : [];
      setStatus({
        loading: false,
        hasExchange: exchanges.length > 0,
        hasLockedAssets: exchanges.some((e) => e.assetsLocked || e.assets_locked),
        hasBot: botList.length > 0,
      });
    } catch (_) {
      setStatus((s) => ({ ...s, loading: false }));
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const value = {
    status,
    refreshStatus,
    welcomeSeen: !!flags.welcomeSeen,
    markWelcomeSeen: useCallback(() => persist({ welcomeSeen: true }), [persist]),
    checklistDismissed: !!flags.checklistDismissed,
    dismissChecklist: useCallback(() => persist({ checklistDismissed: true }), [persist]),
    tourSeen: !!flags.tourSeen,
    tourRunning,
    tourStep,
    startTour: useCallback(() => { setTourStep(0); setTourRunning(true); }, []),
    nextStep: useCallback(() => setTourStep((s) => s + 1), []),
    prevStep: useCallback(() => setTourStep((s) => Math.max(0, s - 1)), []),
    endTour: useCallback(() => { setTourRunning(false); persist({ tourSeen: true }); }, [persist]),
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export default OnboardingProvider;
